import React, { useState } from "react";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Button, DialogProps, IconButton } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { DateTime } from "luxon";

interface DatePickerButtonProps {
  label: string;
  className?: string;
  variant?: "text" | "outlined" | "contained";
  colour?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  defaultDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  iconVariant?: boolean;
  iconButtonSize?: "small" | "medium" | "large";
  // eslint-disable-next-line
  onChange: (date: any) => void;
}

interface DatePickerDialogProps extends DialogProps {
  "data-testid"?: string;
}

const DatePickerButton = ({
  label,
  className,
  variant,
  colour,
  defaultDate,
  minDate,
  maxDate,
  disabled,
  onChange,
  iconVariant,
  iconButtonSize,
}: DatePickerButtonProps) => {
  const variantValue = variant ?? "contained";
  const colourValue = colour ?? "primary";

  // Set default states
  const [open, setOpen] = useState(false);
  const [dateValue, setDateValue] = useState<DateTime | null>(
    defaultDate ? DateTime.fromJSDate(defaultDate) : null
  );

  // Format Date to be used
  const handleAccept = () => {
    const newDateTime = dateValue ? dateValue.toJSDate() : null;
    const currentTime = new Date(Date.now());

    if (newDateTime) {
      newDateTime.setHours(
        defaultDate ? defaultDate.getHours() : currentTime.getHours()
      );
      newDateTime.setMinutes(
        defaultDate ? defaultDate.getMinutes() : currentTime.getMinutes()
      );
      newDateTime.setSeconds(
        defaultDate ? defaultDate.getSeconds() : currentTime.getSeconds()
      );
      newDateTime.setMilliseconds(
        defaultDate
          ? defaultDate.getMilliseconds()
          : currentTime.getMilliseconds()
      );
    }

    // this is to prevent somehow auto accept
    if (
      defaultDate &&
      newDateTime != null &&
      defaultDate.getTime() === newDateTime.getTime()
    ) {
      return;
    }
    onChange(newDateTime);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      {iconVariant ? (
        <IconButton
          data-testid="date-picker-button-icon-main"
          className={className}
          onClick={() => setOpen(true)}
          color={colourValue}
          disabled={disabled}
          size={iconButtonSize}
        >
          <CalendarMonthIcon />
        </IconButton>
      ) : (
        <Button
          data-testid="date-picker-button-main"
          className={className}
          onClick={() => setOpen(true)}
          variant={variantValue}
          color={colourValue}
          fullWidth
          disabled={disabled}
        >
          {label}
        </Button>
      )}
      <MobileDatePicker
        minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
        maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
        value={dateValue}
        onChange={(date: React.SetStateAction<DateTime | null>) =>
          setDateValue(date)
        }
        onAccept={() => handleAccept()}
        open={open}
        onClose={() => setOpen(false)}
        slots={{
          textField: () => <></>,
        }}
        slotProps={{
          dialog: {
            "data-testid": "date-picker-button-dialog",
          } as DatePickerDialogProps,
        }}
      />
    </LocalizationProvider>
  );
};

export default DatePickerButton;
